import axios from "axios";
import apiClient from "./apiClient";
import { getApiErrorMessage } from "../utils/apiError";

interface ExchangeRate {
  id: string;
  fromCurrency: string;
  toCurrency: string;
  buyRate: string;
  sellRate: string;
  createdAt: string;
  updatedAt: string;
  adminId: string | null;
}

interface ExchangeRateHistoryResponse {
  data: ExchangeRate[];
  pagination: {
    totalItems: number;
    currentPage: number;
    totalPages: number;
    limit: number;
  };
}

interface CreateRateRequest {
  fromCurrency: string;
  toCurrency: string;
  buyRate: number;
  sellRate: number;
}

interface UpdateRateRequest {
  fromCurrency: string;
  toCurrency: string;
  buyRate: number;
  sellRate: number;
}

interface ChartDataParams {
  fromCurrency: string;
  toCurrency: string;
  days?: number;
}

export interface ChartDataPoint {
  date: string;
  rate: number;
  timestamp: string;
}

export interface ChartApiResponse {
  period: string;
  data: ChartDataPoint[];
  summary: {
    totalEntries: number;
    latestRate: number | null;
    oldestRate: number | null;
    averageRate: number | null;
  };
}

const RATE_PATH = "rate";

export const createExchangeRate = async (
  data: CreateRateRequest
): Promise<ExchangeRate> => {
  try {
    const response = await apiClient.post<ExchangeRate>(
      `${RATE_PATH}/create`,
      data
    );

    if (!response.data) {
      throw new Error("Invalid response format from server");
    }

    return response.data;
  } catch (error) {
    console.error("Failed to create exchange rate:", error);
    throw new Error(getApiErrorMessage(error, "Failed to create exchange rate"));
  }
};

export const getLatestExchangeRate = async (): Promise<ExchangeRate[]> => {
  try {
    const response = await apiClient.get<ExchangeRate[]>(`${RATE_PATH}`);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return [];
    }
    console.error("Failed to fetch latest exchange rate:", error);
    throw new Error(getApiErrorMessage(error, "Failed to fetch exchange rates"));
  }
};

export const updateExchangeRate = async (
  data: UpdateRateRequest
): Promise<ExchangeRate> => {
  try {
    const response = await apiClient.patch<ExchangeRate>(
      `${RATE_PATH}/update`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("Failed to update exchange rate:", error);
    throw new Error(getApiErrorMessage(error, "Failed to update exchange rate"));
  }
};

export const getExchangeRateHistory = async (
  fromCurrency: string,
  toCurrency: string,
  page = 1,
  limit = 10
): Promise<ExchangeRateHistoryResponse> => {
  try {
    const response = await apiClient.get<ExchangeRateHistoryResponse>(
      `${RATE_PATH}/history`,
      {
        params: { fromCurrency, toCurrency, page, limit },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Failed to fetch exchange rate history:", error);
    throw new Error(
      getApiErrorMessage(error, "Failed to fetch exchange rate history")
    );
  }
};

export const getExchangeRateChartData = async (
  params: ChartDataParams
): Promise<ChartApiResponse> => {
  try {
    const response = await apiClient.get<ChartApiResponse>(`${RATE_PATH}/chart`, {
      params: {
        fromCurrency: params.fromCurrency,
        toCurrency: params.toCurrency,
        days: params.days || 30,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch exchange rate chart data:", error);
    throw new Error(getApiErrorMessage(error, "Failed to load chart data"));
  }
};
